import { Link } from "react-router-dom";
import { ReceiptText, Ticket, CreditCard, Undo2, CalendarX2, Repeat2, Mail, Building2 } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Card, CardContent } from "../components/ui/card";
import { useLang } from "../context/LanguageContext";

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
      <Icon className="w-4.5 h-4.5 text-muted-foreground" aria-hidden="true" />
      {title}
    </h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function TermsPage() {
  const { t } = useLang();
  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs text-muted-foreground mb-4" data-testid="terms-badge">
            <ReceiptText className="w-3.5 h-3.5" />
            {t("Kjøpsvilkår")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight" data-testid="terms-title">
            {t("Vilkår for kjøp")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t("Disse vilkårene gjelder når du kjøper eller reserverer billetter gjennom Right in.")}
          </p>
          <p className="text-xs text-muted-foreground mt-2" data-testid="terms-updated">{t("Sist oppdatert: oktober 2026")}</p>
        </div>

        <Card className="border-border">
          <CardContent className="p-6 sm:p-8 space-y-8">
            <Section icon={Ticket} title={t("Kjøp og levering")} testId="terms-section-purchase">
              <p>{t("Billetter leveres digitalt umiddelbart etter bestilling, via billettlenken og på e-post. Hver billett har en unik QR-kode som skannes ved inngangen og kun kan brukes én gang. Det er fri plassering – ingen nummererte seter.")}</p>
              <p>{t("Arrangøren av arrangementet er selger og ansvarlig for gjennomføringen. Right in leverer billettløsningen på vegne av arrangøren.")}</p>
            </Section>

            <Section icon={CreditCard} title={t("Pris og betaling")} testId="terms-section-payment">
              <p>{t("Prisen som vises ved bestilling er totalprisen – ingen skjulte gebyrer eller gebyrpåslag. Betalte billetter betales med Vipps. Gratisbilletter bekreftes uten betaling.")}</p>
              <p data-testid="terms-pay-at-door-line">
                <strong className="text-foreground">{t("Betal ved inngangen:")}</strong>{" "}
                {t("Når arrangøren tilbyr det, kan du reservere billetter uten å betale på forhånd. Du får QR-billetten med en gang, tydelig merket «ikke betalt», og betaler billettprisen til arrangøren ved inngangen (kontant eller Vipps på stedet). Reservasjonen er bindende på samme måte som et kjøp, og billetten gir adgang først når beløpet er betalt.")}
              </p>
            </Section>

            <Section icon={CalendarX2} title={t("Avlysning og endringer")} testId="terms-section-cancellation">
              <p>{t("Hvis arrangementet avlyses, refunderer arrangøren billettprisen. Hvis arrangementet flyttes til ny dato, gjelder billetten din for den nye datoen – eller du kan be arrangøren om refusjon.")}</p>
            </Section>

            <Section icon={Undo2} title={t("Angrerett og refusjon")} testId="terms-section-refund">
              <p>{t("Billetter til kultur- og fritidsarrangementer på et fastsatt tidspunkt er unntatt fra angreretten (angrerettloven § 22 bokstav m). Kjøpet er derfor bindende, med mindre arrangøren har egne, romsligere refusjonsregler for sitt arrangement.")}</p>
              <p>{t("Spørsmål om refusjon rettes til arrangøren, som du finner på arrangementssiden.")}</p>
            </Section>

            <Section icon={Repeat2} title={t("Videresalg og misbruk")} testId="terms-section-resale">
              <p>{t("Billetter kan deles med eget følge, men kommersielt videresalg til høyere pris er ikke tillatt. Arrangøren kan avvise billetter som er skaffet i strid med vilkårene.")}</p>
            </Section>

            <Section icon={Mail} title={t("Kontakt")} testId="terms-section-contact">
              <p>
                {t("Spørsmål om vilkårene? Send en e-post til")}{" "}
                <a href="mailto:hei@rightin.no" className="underline underline-offset-4 text-foreground" data-testid="terms-contact-email">hei@rightin.no</a>.{" "}
                {t("Se også vår")}{" "}
                <Link to="/personvern" className="underline underline-offset-4 text-foreground" data-testid="terms-privacy-link">{t("personvernerklæring (GDPR)")}</Link>.
              </p>
            </Section>

            <Section icon={Building2} title={t("Om oss")} testId="terms-section-about">
              <p>
                {t("Right in leveres av Kviteberg Digital, en avdeling i Kviteberg Eiendom AS.")}{" "}
                <Link to="/om-oss" className="underline underline-offset-4 text-foreground" data-testid="terms-about-link">{t("Les mer om oss")}</Link>.
              </p>
            </Section>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground mt-6">
          <Link to="/" className="underline underline-offset-4" data-testid="terms-home-link">{t("Tilbake til forsiden")}</Link>
        </p>
      </div>
    </PublicLayout>
  );
}
